import type { ClientSubscriptionUpdateInput, ClientSuspendInput, FeatureDefaults, FeatureOverridesUpdate, SuperAdminClientDetail } from '@repo/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { superAdminClientsService } from '@repo/ui/lib/api/services/superadmin-clients.service';

export const superAdminClientQueryKey = (id: number) => ['superadmin', 'clients', id] as const;

export const useSuperAdminClient = (id: number) =>
  useQuery<SuperAdminClientDetail>({
    queryKey: superAdminClientQueryKey(id),
    queryFn: () => superAdminClientsService.getClient(id),
    enabled: Number.isInteger(id) && id > 0,
  });

/** Actions de la fiche client : la réponse est la fiche à jour, mise directement en cache. */
export const useSuperAdminClientActions = (id: number) => {
  const queryClient = useQueryClient();
  const onSuccess = (client: SuperAdminClientDetail) => {
    queryClient.setQueryData(superAdminClientQueryKey(id), client);
    void queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
  };
  return {
    updateSubscription: useMutation({
      mutationFn: (input: ClientSubscriptionUpdateInput) => superAdminClientsService.updateSubscription(id, input),
      onSuccess,
    }),
    suspend: useMutation({
      mutationFn: (input: ClientSuspendInput) => superAdminClientsService.suspend(id, input),
      onSuccess,
    }),
    reactivate: useMutation({ mutationFn: () => superAdminClientsService.reactivate(id), onSuccess }),
    updateFeatures: useMutation({
      mutationFn: (overrides: FeatureOverridesUpdate) => superAdminClientsService.updateClientFeatures(id, overrides),
      onSuccess,
    }),
  };
};

export const featureDefaultsQueryKey = ['superadmin', 'features'] as const;

/** Valeurs par défaut des fonctionnalités pour tous les clients (espace /superadmin). */
export const useFeatureDefaults = () =>
  useQuery({ queryKey: featureDefaultsQueryKey, queryFn: () => superAdminClientsService.getFeatureDefaults() });

export const useUpdateFeatureDefaults = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (defaults: FeatureDefaults) => superAdminClientsService.updateFeatureDefaults(defaults),
    onSuccess: (result) => {
      queryClient.setQueryData(featureDefaultsQueryKey, result);
      void queryClient.invalidateQueries({ queryKey: ['features', 'me'] });
      void queryClient.invalidateQueries({ queryKey: ['superadmin', 'clients'] });
    },
  });
};

/** Rapport mensuel de consommation IA (espace /superadmin). */
export const useAiUsageReport = (month: string) =>
  useQuery({ queryKey: ['superadmin', 'ai-usage', month], queryFn: () => superAdminClientsService.getAiUsage(month) });

/** Indicateurs de la plateforme (espace /superadmin). */
export const usePlatformMetrics = () =>
  useQuery({ queryKey: ['superadmin', 'metrics'], queryFn: () => superAdminClientsService.getMetrics(), staleTime: 5 * 60_000 });
